/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

// eslint-disable-next-line
import { css, jsx } from "@compiled/react";
import { bind, type UnbindFn } from "bind-event-listener";
import { UnsuccessfulFetchError } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { N0, N80, N800 } from "@atlaskit/theme/colors";
import { N500 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { collectionOfferingIds } from "@/components/signup/utils";
import { OrganizationCard } from "@/templates/buy/cloud/post-order-provisioning/product-card/src/organization-card";
import { ProductCard } from "@/templates/buy/cloud/post-order-provisioning/product-card/src/product-card";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    DisabledSiteNameInputForm,
    type SiteNameDropdownData,
    SiteNameDropdownForm,
    SiteNameFieldForm
} from "./forms";
import { useSiteSetupHeaderDetails } from "./hooks/use-site-setup-header-details";
import {
    type DirectBuySiteSetupProps,
    type DirectBuyNewSiteActivationData,
    DirectBuyStatus
} from "./types";
import { ErrorHero, FormSection, Loading, TopNav } from "./ui";
import { SiteSetupHeader } from "./ui/site-setup-header/index";

const containerStyles = css({
    "backgroundRepeat": "no-repeat",
    "backgroundSize": "contain",
    "height": "100vh",
    "display": "flex",
    "alignItems": "center",
    "flexDirection": "column",
    // This overwrites a global style `svg {margin-bottom: 16px}` set in magnolia
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    "& svg": {
        marginBlockEnd: 0
    },
    // TODO: Use new breakpoints, info at https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples
    "@media (max-width: 664px)": {
        // This property is important to override the inline styling that sets the bg image.
        // Image is received as a prop and therefore cannot be included in the css styles within
        // this object.

        backgroundImage: "none !important"
    }
});

const errorPageContainerStyles = css({
    // This overwrites a global style `svg {margin-bottom: 16px}` set in magnolia
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    "& svg": {
        marginBlockEnd: 0
    }
});

const innerContainerStyles = css({
    "alignItems": "center",
    "display": "flex",
    "flexDirection": "column",
    "height": "calc(100vh - 52px)", // subtracting size of header
    "@media (max-width: 1194px)": {
        width: "100%"
    }
});

const siteSetupContainerStyles = css({
    "height": "665px",
    "backgroundColor": token("elevation.surface.sunken", N0),
    "boxShadow": `0px 8px 12px rgba(9, 30, 66, 0.15), 0px 0px 1px rgba(9, 30, 66, 0.31)`,
    // TODO Delete this comment after verifying space token -> previous value `'3px'`
    "borderRadius": token("radius.small", "3px"),
    "display": "flex",
    "padding": "0",
    "position": "relative",
    "insetBlockStart": token("space.1000", "80px"),
    "width": "1130px",
    "@media (max-width: 1194px)": {
        marginInlineStart: token("space.400", "32px"),
        marginInlineEnd: token("space.400", "32px"),
        maxWidth: "calc(100% - 64px)"
    },
    "@media (max-width: 1024px)": {
        insetBlockStart: token("space.800", "64px")
    },
    "@media (max-width: 768px)": {
        display: "flex",
        flexDirection: "column",
        height: "1055px",
        insetBlockStart: token("space.500", "36px"),
        width: "565px"
    },
    "@media (max-width: 664px)": {
        insetBlockStart: token("space.400", "32px")
    },
    "@media (max-width: 375px)": {
        boxShadow: "none",
        margin: 0,
        maxWidth: "unset",
        insetBlockStart: 0,
        width: "100%"
    }
});

const siteSetupFormContainerStyles = css({
    "boxSizing": "border-box",
    "padding": token("space.800", "64px"),
    "width": "50%",
    "@media (max-width: 1024px)": {
        padding: token("space.400", "32px")
    },
    "@media (max-width: 768px)": {
        height: "auto",
        padding: token("space.800", "64px"),
        width: "100%"
    },
    "@media (max-width: 375px)": {
        padding: token("space.400", "32px")
    }
});

const formImageStyles = css({
    "borderRadius": `0 ${token("radius.small", "3px")} ${token(
        "radius.small",
        "3px"
    )} 0`,
    "height": "100%",
    "width": "50%",
    "@media (max-width: 768px)": {
        borderRadius: `0 0 ${token("radius.small", "3px")} ${token(
            "radius.small",
            "3px"
        )}`,
        height: "665px",
        width: "100%"
    },
    "@media (max-width: 375px)": {
        borderRadius: 0
    }
});

const urlPreviewStyles = css({
    "alignItems": "center",
    "background": token("color.background.input", "#F5F5F7"),
    "color": token("color.text", N800),
    "display": "flex",
    "fontSize": "12px",
    "height": "22px",

    "insetInlineEnd": 0,
    "overflow": "hidden",
    "position": "absolute",

    "insetBlockStart": "137px",
    "whiteSpace": "nowrap",
    "width": "calc(50% - 190px)",
    "zIndex": 1,
    "@media (max-width: 1194px)": {
        insetInlineStart: "calc(50% + 190px)"
    },
    "@media (max-width: 768px)": {
        insetInlineStart: "190px",

        insetBlockStart: "527px",
        width: "calc(100% - 190px)"
    }
});

const urlPrefixStyles = css({
    color: token("color.text.subtlest", N80)
});

function createBackgroundStyles(backgroundImageUrl: string) {
    return {
        backgroundImage: `url(${backgroundImageUrl})`
    };
}

export const DirectBuySiteSetup = ({
    imageAssets,
    error,
    getSiteNameAvailability,
    orderId,
    sites,
    siteFlags,
    testId,
    transactionAccount,
    activateForExistingSite,
    activateForNewSite,
    pollDirectBuyCloudStatus,
    traceId,
    isUserLoggedIn,
    isLoading,
    directBuyStatus,
    email,
    activateForExistingOrg,
    provisionableProducts,
    preselectedOrgSiteIds,
    orgName
}: // eslint-disable-next-line complexity
DirectBuySiteSetupProps) => {
    const searchParams = new URLSearchParams(window.location.search);
    const offeringId = searchParams.get("offeringId") || "";
    const isCollection = collectionOfferingIds.has(offeringId);
    // If locked-in flow for collection with provisionable offerings and no matching existing site, show conflicting error
    const isLockedInOrderConflictError =
        siteFlags?.useLockedInSite &&
        !sites.length &&
        !!siteFlags?.provisionableSiteOfferings?.length;
    const orderSlug =
        sites[0]?.orderContext?.slug ||
        siteFlags?.productConflictingSites?.[0]?.orderContext?.slug ||
        "";

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [newSite, setNewSite] = useState<boolean>(false);
    const chooseSite = (newSite: boolean) => {
        setNewSite(newSite);
    };
    const [showConflictError, setShowConflictError] = useState<boolean>(
        !!isLockedInOrderConflictError
    );

    const siteHeaderDetails = useSiteSetupHeaderDetails(directBuyStatus);

    const orgId = searchParams.get("orgId");
    const isDeferredOrgActivation =
        checkFeatureFlagGate(featureFlags.signupPopBackBtn) && orgId;

    const showBackButton =
        isDeferredOrgActivation && window.history && window.history.length > 1;

    const getTraceId = (error: UnsuccessfulFetchError): string | null => {
        return error.response?.headers?.get?.("Atl-Traceid");
    };

    if (isLockedInOrderConflictError) {
        createAnalyticsEvent({
            eventName: "lockedInOrderConflictError",
            eventComponent: "direct-buy-site-setup",
            event: "viewed",
            eventType: "ui",
            attributes: {
                orderId,
                lockedInSiteName: siteFlags.lockedInSiteName,
                provisionableSiteOfferingIds:
                    siteFlags.provisionableSiteOfferings?.map(
                        (offering) => offering.offeringId
                    )
            }
        }).fire("signup");
    }

    const handleActivateForExistingOrg = async () => {
        if (isDeferredOrgActivation) {
            const { provisioningRequestId } = await activateForExistingOrg({
                orgId,
                orderId,
                transactionAccountId: transactionAccount
            });
            return await pollDirectBuyCloudStatus(provisioningRequestId);
        }
        return true;
    };

    const handleSubmitDropdown = useCallback(
        // @ts-ignore
        async (dropdownData: SiteNameDropdownData) => {
            const success = await handleActivateForExistingOrg();
            if (!success) {
                return;
            }
            const site = sites.find(
                (site) => site.siteName === dropdownData.siteName.value
            );
            if (!site) {
                throw new Error(
                    `site missing from sites data: ${dropdownData.siteName}`
                ); // todo: handle error with state and ui-message
            }

            try {
                await activateForExistingSite({
                    cloudId: site.cloudId as string
                });
            } catch (error) {
                // https://atlassian.design/components/form/examples#submission-validation
                // For submission errors, the onSubmit handler should return an object
                const responseTraceId = getTraceId(
                    error as UnsuccessfulFetchError
                );
                return {
                    siteName: `Sorry, an unknown error occurred. If this keeps happening, contact support with trace ID: ${
                        responseTraceId ?? "unknown"
                    }`
                };
            }
        },
        [sites, activateForExistingSite]
    );

    // @ts-ignore
    const handleSubmitSingleSite = useCallback(async () => {
        const success = await handleActivateForExistingOrg();
        if (!success) {
            return;
        }

        const site = sites[0];
        try {
            await activateForExistingSite({
                cloudId: site?.cloudId as string
            });
        } catch (error) {
            // https://atlassian.design/components/form/examples#submission-validation
            // For submission errors, the onSubmit handler should return an object
            const responseTraceId = getTraceId(error as UnsuccessfulFetchError);
            return {
                siteName: `Sorry, an unknown error occurred. If this keeps happening, contact support with trace ID: ${
                    responseTraceId ?? "unknown"
                }`
            };
        }
    }, [sites, activateForExistingSite]);

    const handleSubmitNewSite = useCallback(
        // @ts-ignore
        async (data: DirectBuyNewSiteActivationData) => {
            const success = await handleActivateForExistingOrg();
            if (!success) {
                return;
            }
            try {
                await activateForNewSite(data);
            } catch (error) {
                // https://atlassian.design/components/form/examples#submission-validation
                // For submission errors, the onSubmit handler should return an object
                const responseTraceId = getTraceId(
                    error as UnsuccessfulFetchError
                );
                return {
                    siteName: `Sorry, an unknown error occurred. If this keeps happening, contact support with trace ID: ${
                        responseTraceId ?? "unknown"
                    }`
                };
            }
        },
        [activateForNewSite]
    );

    const getDomainName = (url: string): string => {
        const trimmedUrl = url.trim().toLowerCase();
        if (trimmedUrl.startsWith("http://")) {
            return trimmedUrl.split("http://")[1] ?? "";
        } else if (trimmedUrl.startsWith("https://")) {
            return trimmedUrl.split("https://")[1] ?? "";
        } else {
            return url;
        }
    };

    const renderPreview = useCallback(
        (urlPreview: string) => (
            <span id="url-preview" css={urlPreviewStyles}>
                <span css={urlPrefixStyles}>
                    {urlPreview.startsWith("http://") ? "http://" : "https://"}
                </span>
                {getDomainName(urlPreview)}
            </span>
        ),
        []
    );

    const handleConflictSiteCheck = useCallback(
        (value: string) => {
            const isSiteCheckConflictError =
                !!value &&
                siteFlags?.productConflictingSites?.some(
                    ({ url }) => url === value
                );
            setShowConflictError(!!isSiteCheckConflictError);
        },
        [siteFlags]
    );

    const provisionableProductCard = useMemo<ReactElement | undefined>(() => {
        return !!provisionableProducts &&
            provisionableProducts.length > 0 &&
            provisionableProducts.length < 2 ? (
            <FormSection>
                <span
                    css={css({
                        color: token("color.text.subtle", N500),
                        fontFamily: token("font.family.brand.body"),
                        fontStyle: "normal",
                        fontWeight: token("font.weight.medium"),
                        fontSize: "14px",
                        lineHeight: "15px",
                        display: "block",
                        marginBlockEnd: token("space.100", "8px")
                    })}
                >
                    App
                </span>
                <div
                    css={css({
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.300", "24px"),
                        width: "100%",
                        paddingBlockEnd: token("space.100", "8px")
                    })}
                >
                    {provisionableProducts.map((product, index) => (
                        <ProductCard
                            key={`${product.productName}-${product.tier}-${index}`}
                            productName={product.productName}
                            tier={product.tier}
                            includedProductKeys={product.includedProductKeys}
                        />
                    ))}
                </div>
            </FormSection>
        ) : undefined;
    }, [provisionableProducts]);

    const organizationCard = useMemo<ReactElement | undefined>(() => {
        return orgName ? (
            <FormSection>
                <span
                    css={css({
                        color: token("color.text.subtle", N500),
                        fontFamily: token("font.family.brand.body"),
                        fontStyle: "normal",
                        fontWeight: token("font.weight.medium"),
                        fontSize: "14px",
                        lineHeight: "15px",
                        marginBlockEnd: token("space.100")
                    })}
                >
                    Organization
                </span>
                <div
                    css={css({
                        display: "flex",
                        flexDirection: "column",
                        gap: token("space.300", "24px"),
                        width: "100%",
                        paddingBlockEnd: token("space.100", "8px")
                    })}
                >
                    <OrganizationCard organizationName={orgName} />
                </div>
            </FormSection>
        ) : undefined;
    }, [orgName]);

    const siteNameInput = useMemo<ReactElement>(() => {
        const siteNameFieldForm = (
            <SiteNameFieldForm
                getSiteNameAvailability={getSiteNameAvailability}
                newSite={chooseSite}
                onSubmit={handleSubmitNewSite}
                renderPreview={renderPreview}
                sites={sites.map(({ siteName }) => ({
                    label: getDomainName(siteName),
                    value: siteName
                }))}
                value={""}
                provisionableProductCard={provisionableProductCard}
                organizationCard={organizationCard}
            />
        );
        const disabledSiteNameInputForm = (
            <DisabledSiteNameInputForm
                newSite={siteFlags?.useLockedInSite ? undefined : chooseSite}
                error={{
                    isConflictError: isLockedInOrderConflictError
                }}
                onSubmit={handleSubmitSingleSite}
                renderPreview={renderPreview}
                value={sites[0] ? getDomainName(sites[0].siteName) : ""}
                flags={{
                    lockedInSiteName: siteFlags?.lockedInSiteName,
                    orderSlug
                }}
                provisionableProductCard={provisionableProductCard}
                organizationCard={organizationCard}
            />
        );

        // Show disabled site name input form if there is a conflict error
        if (isLockedInOrderConflictError && sites.length === 0) {
            return disabledSiteNameInputForm;
        }

        if (sites.length === 0 || newSite) {
            return siteNameFieldForm;
        } else if (sites.length === 1 && sites[0]) {
            return disabledSiteNameInputForm;
        } else {
            let dropdownSites = sites.map(({ siteName }) => ({
                value: siteName,
                label: getDomainName(siteName)
            }));
            // Append org specific conflicting sites to sites
            if (siteFlags?.productConflictingSites) {
                dropdownSites = dropdownSites.concat(
                    siteFlags.productConflictingSites
                        .map(({ cloudId, url }) => {
                            const filteredByPreselectedOrgSiteIds =
                                !preselectedOrgSiteIds ||
                                (cloudId &&
                                    preselectedOrgSiteIds.includes(cloudId));
                            if (filteredByPreselectedOrgSiteIds) {
                                return {
                                    value: url,
                                    label: getDomainName(url)
                                };
                            }
                        })
                        .filter((site) => site !== undefined)
                );
            }
            return (
                <SiteNameDropdownForm
                    newSite={chooseSite}
                    onSubmit={handleSubmitDropdown}
                    renderPreview={renderPreview}
                    sites={dropdownSites}
                    onChange={handleConflictSiteCheck}
                    error={{
                        isConflictError: showConflictError
                    }}
                    provisionableProductCard={provisionableProductCard}
                    organizationCard={organizationCard}
                />
            );
        }
    }, [
        sites,
        newSite,
        getSiteNameAvailability,
        handleSubmitNewSite,
        renderPreview,
        handleSubmitSingleSite,
        siteFlags,
        showConflictError,
        handleSubmitDropdown,
        provisionableProductCard,
        organizationCard
    ]);

    useEffect(() => {
        function handleResize() {
            const urlPreview = document.getElementById("url-preview");
            const form = document.getElementById("form");

            if (!form || !urlPreview) {
                return;
            }

            if (window.innerWidth > 768) {
                urlPreview.style.top = "";
                return;
            }

            const { height } = form.getBoundingClientRect();
            urlPreview.style.top = `${height + 137}px`;
        }

        const unbind: UnbindFn = bind(window, {
            type: "resize",
            listener: handleResize
        });

        return unbind;
    }, []);

    if (!isUserLoggedIn || isLoading) {
        return <Loading message="Loading..." />;
    }

    const restrict =
        siteFlags?.useLockedInSite && !sites.length && !isCollection;
    if (error || !orderId || !transactionAccount || restrict) {
        if (restrict) {
            console.error("user is not allowed to provision this site");
        }

        return (
            <div css={errorPageContainerStyles}>
                {!!isUserLoggedIn && <TopNav />}
                <ErrorHero errorImage={imageAssets.error} />
                {!!error && traceId && (
                    <h3>
                        <p>
                            If this keeps happening, contact support with trace
                            ID: {traceId}
                        </p>
                    </h3>
                )}
            </div>
        );
    }

    return (
        <div
            style={createBackgroundStyles(imageAssets.background)}
            css={containerStyles}
            data-testid={testId}
        >
            {!!isUserLoggedIn && <TopNav />}
            <div css={innerContainerStyles}>
                {siteHeaderDetails && (
                    <SiteSetupHeader
                        title={siteHeaderDetails.title}
                        description={siteHeaderDetails.description}
                        descriptionHighlight={
                            directBuyStatus === DirectBuyStatus.ORDER_CONFIRMED
                                ? email
                                : ""
                        }
                    />
                )}
                <div css={siteSetupContainerStyles}>
                    <div id="form" css={siteSetupFormContainerStyles}>
                        {showBackButton && (
                            <button
                                onClick={() => window.history.back()}
                                aria-label="Go Back"
                                css={css({
                                    paddingBlockEnd: token("space.200", "16px"),
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer"
                                })}
                            >
                                ←
                            </button>
                        )}
                        {siteNameInput}
                    </div>
                    <div
                        css={formImageStyles}
                        style={createBackgroundStyles(imageAssets.browser)}
                    />
                </div>
            </div>
            {!!error && traceId && (
                <h3>
                    <p>
                        If this keeps happening, contact support with trace ID:
                        {traceId}
                    </p>
                </h3>
            )}
        </div>
    );
};
